/**
 * useClearHistory hook
 *
 * Wraps the `clear-history` CF Worker handler in a React Query mutation.
 * The handler deletes all of the caller's `agent_executions` rows and
 * invalidates the matching Redis history cache, then returns the row count.
 *
 * Wire contract validated via `ClearAnalysisResultDto` from
 * `@dramadetector/shared/schemas/analysis` using `fetchJsonValidated()` —
 * shared with the (separate) `clear-analysis-history` handler so the two
 * can be unified in a follow-up without a wire-format change.
 *
 * This hook replaces the direct `authenticatedFetch(getEdgeFunctionUrl(
 * 'clear-history'), ...)` call that previously lived inside
 * `lib/settings/historyActions.ts` — routing all clear-history traffic
 * through one validated path keeps the `{ deletedCount }` contract
 * enforceable end-to-end.
 */
import { useMutation } from '@tanstack/react-query';
import {
  ClearAnalysisResultDto,
  type ClearAnalysisResult,
} from '@dramadetector/shared/schemas';
import { fetchJsonValidated } from '@dramadetector/api-client';
import { getEdgeFunctionUrl } from '../lib/api-url';

export type { ClearAnalysisResult };

/** Inputs accepted by `useClearHistory`. */
export interface ClearHistoryInput {
  /** Owner user id (required by the worker; also used for logging parity). */
  userId: string;
}

async function postClearHistory(input: ClearHistoryInput): Promise<ClearAnalysisResult> {
  return fetchJsonValidated(ClearAnalysisResultDto, getEdgeFunctionUrl('clear-history'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId: input.userId }),
  });
}

/** React Query mutation hook for `clear-history`. */
export function useClearHistory() {
  return useMutation({
    mutationFn: (input: ClearHistoryInput) => postClearHistory(input),
  });
}
